import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import {
  ProductionDayBatch,
  Recipe,
  Product,
  ProductionPhaseType,
  BatchMaterialLotRow,
} from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import MesDateSelector, { getDefaultMesDate, saveMesDate } from "../../../components/MES/MesDateSelector";
import {
  buildWorkOrderDocument,
  toProductPhaseLite,
  batchHasPhase,
  resolvePhaseTimes,
  phaseMachineName,
  PHASE_DISPLAY,
  PHASE_ORDER,
  ProductPhaseMap,
  LotsByBatch,
} from "./workOrderTemplate";
import { getRecipeLineName } from "../../../utils/recipeBatchLines";
import { isAbsoluteRecipe } from "../../../utils/recipeQuantity";
import { formatClockTime } from "../../../utils/processParameterSerialize";

/**
 * Work Orders Page — view and print per-phase work orders for production.
 *
 * Phases are driven by the dynamic production-phase model (ProductionPhaseType /
 * ProductionPhase): the tab bar is built from the phase-type catalog rather than
 * the legacy fixed mixer/processor/oven trio. Each phase's times/machine are
 * resolved from the product's configured phases, falling back to the legacy
 * schedule columns when no configuration exists.
 */
const WorkOrders = () => {
  savePathToLocalStorage();

  const [batches, setBatches] = useState<ProductionDayBatch[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(getDefaultMesDate());
  const [selectedPhase, setSelectedPhase] = useState<string>("mixing");
  const [loading, setLoading] = useState(false);
  const [recipes, setRecipes] = useState<Record<number, Recipe>>({});
  const [products, setProducts] = useState<Record<number, Product>>({});
  const [productPhases, setProductPhases] = useState<ProductPhaseMap>({});
  const [phaseTypes, setPhaseTypes] = useState<ProductionPhaseType[]>([]);
  /** Lotovi sirovina po zamjesu — kolona LOT na nalogu za miksanje. */
  const [lotsByBatch, setLotsByBatch] = useState<LotsByBatch>({});

  const handleDateChange = (date: string) => {
    saveMesDate(date);
    setSelectedDate(date);
  };

  useEffect(() => {
    loadProductionDay();
  }, [selectedDate]);

  // Load the phase-type catalog once (drives the tab bar / phase names).
  useEffect(() => {
    mesApi
      .getProductionPhaseTypes()
      .then((types) => setPhaseTypes(types.filter((t) => t.Code)))
      .catch((e) => console.error("Failed to load production phase types", e));
  }, []);

  // Keep the selected phase valid against the loaded catalog.
  useEffect(() => {
    if (phaseTypes.length === 0) return;
    const codes = phaseTypes.map((t) => t.Code as string);
    if (!codes.includes(selectedPhase)) setSelectedPhase(codes[0]);
  }, [phaseTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Everything physically produced on the selected day, including semi-finished
  // (starter, enzimi…) made today for other days' plans.
  const loadProductionDay = async () => {
    setLoading(true);
    try {
      const { batches: dayBatches } = await mesApi.getProductionDay(selectedDate);
      setBatches(dayBatches);
      setLoaded(true);

      const productIds = Array.from(
        new Set(dayBatches.map((b) => b.ProductID).filter((id): id is number => !!id && id > 0))
      );

      // Recipes (for the mixing nalog) + per-product phase configuration (for
      // phase membership, machine assignment, temperatura/trajanje per phase).
      const recipeMap: Record<number, Recipe> = {};
      const phaseMap: ProductPhaseMap = {};
      for (const pid of productIds) {
        try {
          // with_ingredients: the batch lines and the printed work order name every
          // line, either by its ingredient or by its semi-finished product.
          const recipeList = await mesApi.getRecipes({ product_id: pid, is_active: true, with_ingredients: true });
          if (recipeList.length > 0) recipeMap[pid] = recipeList[0];
        } catch (e) {
          console.error("Failed to load recipe for product", pid);
        }
        try {
          const phases = await mesApi.getProductPhases(pid);
          phaseMap[pid] = toProductPhaseLite(phases);
        } catch (e) {
          console.error("Failed to load phases for product", pid);
        }
      }
      setRecipes(recipeMap);
      setProductPhases(phaseMap);

      // Product details (odvaga / temperatura / vrijeme pečenja) for the naloge.
      try {
        const productList = await mesApi.getProducts();
        const productMap: Record<number, Product> = {};
        for (const p of productList) productMap[p.ID] = p;
        setProducts(productMap);
      } catch (e) {
        console.error("Failed to load products", e);
      }

      // Lotovi sirovina potvrđeni u planiranju (i naknadno izmijenjeni u
      // pogonu) — bez njih je kolona LOT na ispisu naloga ostajala prazna, pa
      // je miješač morao lotove prepisivati s papira iz planiranja. Jedan
      // zahtjev za cijeli dan, ne po zamjesu.
      try {
        const lotRows = await mesApi.getBatchMaterialLots(dayBatches.map((b) => b.ID));
        const grouped: LotsByBatch = {};
        for (const row of lotRows) {
          (grouped[row.batchId] ||= [] as BatchMaterialLotRow[]).push(row);
        }
        setLotsByBatch(grouped);
      } catch (e) {
        console.error("Failed to load batch material lots", e);
        setLotsByBatch({});
      }
    } catch (err: any) {
      toast.error("Failed to load timeline: " + err.message);
      setBatches([]);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr?: string) => formatClockTime(dateStr);

  // Tabs come from the phase-type catalog; fall back to the canonical order.
  const phaseTabs: { code: string; label: string }[] =
    phaseTypes.length > 0
      ? phaseTypes.map((t) => ({
          code: t.Code as string,
          label: t.Name || PHASE_DISPLAY[t.Code as string]?.name || (t.Code as string),
        }))
      : PHASE_ORDER.map((code) => ({ code, label: PHASE_DISPLAY[code].name }));

  const getBatchesByPhase = () =>
    batches
      .filter((b) => batchHasPhase(b, selectedPhase, productPhases))
      .sort((a, b) => {
        const sa = resolvePhaseTimes(a, selectedPhase).start;
        const sb = resolvePhaseTimes(b, selectedPhase).start;
        if (sa && sb) return new Date(sa).getTime() - new Date(sb).getTime();
        if (sa) return -1;
        if (sb) return 1;
        return (a.Sequence ?? 0) - (b.Sequence ?? 0);
      });

  const phaseDuration = (batch: ProductionDayBatch): number | null => {
    const cfg = (batch.ProductID != null && productPhases[batch.ProductID]) || [];
    const fromCfg = cfg.find((p) => p.code === selectedPhase)?.durationMin;
    if (fromCfg != null) return fromCfg;
    switch (PHASE_DISPLAY[selectedPhase]?.legacy) {
      case "mixing": return batch.MixingTimeMin ?? null;
      case "fermentation": return batch.BulkFermentationMin ?? null;
      case "processing": return batch.ProcessingTimeMin ?? null;
      case "final_proof": return batch.FinalProofMin ?? null;
      case "baking": return batch.BakingTimeMin ?? null;
      default: return null;
    }
  };

  // Scale an ingredient to the desired dough mass by its share of the TOTAL baker %.
  // Recipes are expressed in baker's percentages where flour = 100, so the column
  // sums to well over 100 (100 flour + hydration + salt + …). Each ingredient mass is
  // therefore (bakerPct / Σ all %) * doughMass — NOT (bakerPct/100) * doughMass, which
  // assumed the column summed to 100 and inflated every value by ~Σ%/100 (≈2×).
  const calculateIngredientMass = (bakerPct: number, totalMass: number, sumPct: number) => {
    const factor = sumPct > 0 ? totalMass / sumPct : 0;
    return (bakerPct * factor).toFixed(3);
  };

  // Sum of all baker percentages for a recipe (denominator for mass scaling).
  const recipeSumPct = (recipe: any): number =>
    (recipe?.RecipeIngredient_RecipeToRecipeIngredient_Recipe ?? [])
      .reduce((sum: number, ing: any) => sum + (Number(ing.BakerPercentage) || 0), 0);

  /**
   * Dodijeljeni lot jedne recepturne stavke ove zamjese. Sirovine se vežu po
   * ID-u, poluproizvodi po nazivu (u mikser ulaze kao lot vlastite zamjese i
   * na utrošku nemaju Ingredient).
   */
  const lotLabelForLine = (batchId: number, ing: any): string => {
    const rows = lotsByBatch[batchId] ?? [];
    const name = getRecipeLineName(ing).trim().toLowerCase();
    const matched = rows.filter((r) =>
      ing.Ingredient != null && r.ingredientId != null
        ? r.ingredientId === ing.Ingredient
        : (r.material || "").trim().toLowerCase() === name
    );
    const labels = matched
      .map((r) => r.lotNumber || (r.materialLotId ? `#${r.materialLotId}` : ""))
      .filter(Boolean);
    return labels.length > 0 ? Array.from(new Set(labels)).join(", ") : "—";
  };

  const handlePrint = () => {
    const phaseBatches = getBatchesByPhase();
    if (phaseBatches.length === 0) {
      toast.info("Nema zamjesa za ispis za odabranu fazu.");
      return;
    }

    const html = buildWorkOrderDocument({
      phaseCode: selectedPhase,
      dateStr: selectedDate,
      batches: phaseBatches,
      recipes,
      products,
      productPhases,
      phaseTypes,
      lotsByBatch,
    });

    const printWindow = window.open("", "_blank", "width=900,height=1100");
    if (!printWindow) {
      toast.error("Otvaranje ispisa blokirano. Dozvolite skočne prozore (pop-ups).");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const phaseBatches = getBatchesByPhase();
  const machineWord = PHASE_DISPLAY[selectedPhase]?.usesMachine ? "Stroj" : "Faza";

  return (
    <div className="mes-page work-orders-page">
      <Header title="MES - Radni nalozi" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          {/* Date Selector */}
          <div className="no-print">
            <MesDateSelector
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              showTimelineIndicators={true}
            />
          </div>

          {/* Controls */}
          <div className="mes-section work-orders-controls no-print">
            <div className="tab-buttons">
              {phaseTabs.map((tab) => (
                <button
                  key={tab.code}
                  className={`tab-btn ${selectedPhase === tab.code ? "active" : ""}`}
                  onClick={() => setSelectedPhase(tab.code)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <button className="btn-print" onClick={handlePrint}>
              Print
            </button>
          </div>

          {/* Work Orders */}
          {phaseBatches.length > 0 ? (
            <div className="work-orders-list">
              {phaseBatches.map((batch, index) => {
                const times = resolvePhaseTimes(batch, selectedPhase);
                const machine = phaseMachineName(batch, selectedPhase, productPhases);
                const duration = phaseDuration(batch);
                return (
                  <div key={batch.ID} className="work-order-card">
                    <div className="work-order-header">
                      <div className="order-number">#{index + 1}</div>
                      <div className="order-id">{batch.MixCode}</div>
                      <div className="order-product">{batch.ProductName}</div>
                      <div className="order-mass">{batch.FinalMassKg} kg</div>
                    </div>

                    <div className="work-order-times">
                      <div className="time-item">
                        <span className="label">Pocetak:</span>
                        <span className="value">{formatTime(times.start)}</span>
                      </div>
                      <div className="time-item">
                        <span className="label">Kraj:</span>
                        <span className="value">{formatTime(times.end)}</span>
                      </div>
                      {duration != null && (
                        <div className="time-item">
                          <span className="label">Trajanje:</span>
                          <span className="value">{duration} min</span>
                        </div>
                      )}
                    </div>

                    {/* Recipe ingredients — only meaningful for the mixing phase */}
                    {selectedPhase === "mixing" && batch.ProductID && recipes[batch.ProductID] && (
                      <div className="work-order-recipe">
                        <h4>Recept</h4>
                        {isAbsoluteRecipe(recipes[batch.ProductID]) ? (
                          // Masa se racuna iz zbroja postotaka, kojeg slasticarska
                          // receptura nema — tablica bi bila puna nula.
                          <p className="modal-hint">
                            Receptura je u apsolutnim kolicinama (g / kom / list). Prikaz mase po
                            zamjesu za slasticarske recepture jos nije podrzan.
                          </p>
                        ) : (
                        <div className="mes-table-wrap">
                          <table className="recipe-table">
                          <thead>
                            <tr>
                              <th>Sastojak</th>
                              <th>%</th>
                              <th>Masa (kg)</th>
                              <th>LOT</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recipes[batch.ProductID]?.RecipeIngredient_RecipeToRecipeIngredient_Recipe?.map((ing) => (
                              <tr key={ing.ID}>
                                <td>{getRecipeLineName(ing)}</td>
                                <td>{ing.BakerPercentage}%</td>
                                <td>
                                  {calculateIngredientMass(
                                    ing.BakerPercentage,
                                    Number(batch.FinalMassKg) || 0,
                                    recipeSumPct(recipes[batch.ProductID])
                                  )}
                                </td>
                                <td>{lotLabelForLine(batch.ID, ing)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                        )}
                      </div>
                    )}

                    {machine && (
                      <div className="work-order-machine">
                        <span>
                          {machineWord}: {machine}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mes-section empty-state">
              {loading ? (
                <p>Ucitavanje...</p>
              ) : loaded ? (
                <p>Nema zamjesa za ovu fazu na {selectedDate}</p>
              ) : (
                <p>Nema timeline-a za {selectedDate}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default WorkOrders;
